/**
 * Resolves a base image URL to a full CDN URL.
 *
 * TCGPlayer CDN URLs are stored without a size suffix in the DB
 * (e.g. "https://tcgplayer-cdn.tcgplayer.com/product/671562").
 * This function appends the highest quality suffix (_in_1000x1000).
 *
 * Non-TCGPlayer URLs (e.g. optcgapi.com) are returned as-is.
 */
function resolveImageUrl(baseUrl: string | null | undefined): string | null {
  if (!baseUrl) return null

  if (baseUrl.includes('tcgplayer-cdn')) {
    return `${baseUrl}_in_1000x1000.jpg`
  }

  return baseUrl
}

/**
 * Recursively walks a JSON response and resolves all `image_url` fields.
 *
 * NOTE: the `image_url` match below is intentionally still snake_case. This
 * runs inside `services/api/client.ts` on the RAW response body, BEFORE
 * `deepCamelize` runs — so at this point the wire's `image_url` key has not
 * yet been converted to `imageUrl`. Flipping this matcher to camelCase would
 * make it silently stop matching anything. Everything else in this file that
 * faces app code (the exported helpers' inputs/outputs) is plain camelCase.
 */
export function resolveImageUrls<T>(data: T): T {
  if (data === null || data === undefined || typeof data !== 'object') {
    return data
  }

  if (Array.isArray(data)) {
    return data.map(resolveImageUrls) as T
  }

  const result = { ...data } as Record<string, unknown>
  for (const key in result) {
    if (key === 'image_url' && typeof result[key] === 'string') {
      result[key] = resolveImageUrl(result[key] as string)
    } else if (typeof result[key] === 'object' && result[key] !== null) {
      result[key] = resolveImageUrls(result[key])
    }
  }
  return result as T
}
